import { Component, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { HeaderComponent } from '../../shared/components/header/header.component';

export interface ModelTokenBreakdown {
  input: number;
  output: number;
  total: number;
}

export interface ModelUsageCard {
  modelId: string;
  name: string;
  problemStatement1: ModelTokenBreakdown;
  problemStatement2: ModelTokenBreakdown;
}

@Component({
  selector: 'app-models',
  standalone: true,
  imports: [HeaderComponent, AssetHeaderComponent],
  templateUrl: './models.component.html',
  styleUrl: './models.component.scss',
})
export class ModelsComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private copyClearTimer?: ReturnType<typeof setTimeout>;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Models' },
    ],
    title: 'Models',
    description:
      'Browse, run, and reuse AI models built to address real-world challenges across sectors.',
  };

  modelCards: ModelUsageCard[] = [
    {
      modelId: 'ministral-3b-3.0',
      name: 'Ministral 3B 3.0',
      problemStatement1: { input: 40_00_000, output: 3_00_000, total: 43_00_000 },
      problemStatement2: { input: 50_00_000, output: 5_00_000, total: 55_00_000 },
    },
    {
      modelId: 'ministral-8b-3.0',
      name: 'Ministral 8B 3.0',
      problemStatement1: { input: 40_00_000, output: 2_50_000, total: 42_50_000 },
      problemStatement2: { input: 50_00_000, output: 3_50_000, total: 53_50_000 },
    },
    {
      modelId: 'nvidia-nemotron-3-nano-30b-a3b',
      name: 'NVIDIA Nemotron 3 Nano 30B A3B',
      problemStatement1: { input: 40_00_000, output: 2_50_000, total: 42_50_000 },
      problemStatement2: { input: 50_00_000, output: 3_50_000, total: 53_50_000 },
    },
    {
      modelId: 'gemma-3-12b',
      name: 'Gemma 3 12B',
      problemStatement1: { input: 40_00_000, output: 2_50_000, total: 42_50_000 },
      problemStatement2: { input: 50_00_000, output: 3_50_000, total: 53_50_000 },
    },
    {
      modelId: 'gemma-3-4b',
      name: 'Gemma 3 4B',
      problemStatement1: { input: 40_00_000, output: 3_00_000, total: 43_00_000 },
      problemStatement2: { input: 50_00_000, output: 5_00_000, total: 55_00_000 },
    },
  ];

  formatIn(value: number): string {
    return new Intl.NumberFormat('en-IN').format(value);
  }

  copiedModelId: string | null = null;

  ngOnDestroy(): void {
    if (this.copyClearTimer) {
      clearTimeout(this.copyClearTimer);
    }
  }

  copyModelId(modelId: string): void {
    navigator.clipboard.writeText(modelId).then(
      () => {
        this.copiedModelId = modelId;
        if (this.copyClearTimer) {
          clearTimeout(this.copyClearTimer);
        }
        this.copyClearTimer = setTimeout(() => {
          this.copiedModelId = null;
          this.cdr.markForCheck();
        }, 2000);
        this.cdr.markForCheck();
      },
      err => {
        console.error('Failed to copy model id:', err);
      }
    );
  }
}
